"use client";

import { useEffect, useState } from "react";
import gsap from "gsap";
import "./LoadingScreen.css";
import { useLoading } from "./LoadingProvider";

export default function LoadingScreen() {
  const { hasLoaded, setHasLoaded } = useLoading();
  const [shouldPlay] = useState(!hasLoaded);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (shouldPlay) {
      setHasLoaded(true);
    }
  }, [shouldPlay, setHasLoaded]);

  useEffect(() => {
    if (!shouldPlay) return;

    const animate = async () => {
      const loadingText = document.getElementById("loading-text");
      const line1 = document.getElementById("loading-line-1");
      const line2 = document.getElementById("loading-line-2");
      const overlay = document.getElementById("overlay-layer");
      const realLogoFull = document.querySelector(".logo-full") as HTMLElement;
      const realLogoShort = document.querySelector(".logo-short") as HTMLElement;
      const logoContainer = document.querySelector(".logo") as HTMLElement;

      if (!loadingText || !line1 || !line2 || !overlay) return;

      // Helper to create character slots
      const createCharSlot = () => {
        const wrapper = document.createElement("div");
        wrapper.className = "char-wrapper";

        const slot = document.createElement("div");
        slot.className = "char-slot";
        slot.style.opacity = "0";
        slot.textContent = "0";

        wrapper.appendChild(slot);
        return { wrapper, slot };
      };

      // Build the text lines
      const buildLine = (container: HTMLElement, text: string) => {
        container.innerHTML = "";
        container.className = "loading-line";
        const slots: HTMLElement[] = [];

        for (let char of text) {
          const { wrapper, slot } = createCharSlot();
          container.appendChild(wrapper);
          // Store the final char on the element for easy access
          slot.dataset.final = char;
          slots.push(slot);
        }
        return slots;
      };

      const slots1 = buildLine(line1, "the binary");
      const slots2 = buildLine(line2, "collective");
      const allSlots = [...slots1, ...slots2];

      // Animation Logic
      const binaryChars = "01";
      const asciiChars = "!@#$%^&*<>[]{}";

      const getRandomBinary = () =>
        binaryChars[Math.floor(Math.random() * binaryChars.length)];
      const getRandomAscii = () =>
        asciiChars[Math.floor(Math.random() * asciiChars.length)];

      // Animate a single character through the sequence
      const animateChar = async (slot: HTMLElement, delay: number) => {
        const finalChar = slot.dataset.final;
        if (finalChar === " ") {
          slot.innerHTML = "&nbsp;";
          slot.style.opacity = "1";
          return;
        }

        // Initial delay
        await new Promise((r) => setTimeout(r, delay * 1000));

        // Define the sequence of contents
        const sequence = [
          getRandomBinary(), // State 1
          getRandomBinary(), // State 2
          getRandomAscii(), // State 3
          finalChar, // State 4 (Final)
        ];

        for (let i = 0; i < sequence.length; i++) {
          const content = sequence[i];

          // If it's not the very first appearance, we need to shrink out first
          if (i > 0) {
            // Shrink & Fade Out
            await gsap.to(slot, {
              scale: 0.8,
              opacity: 0,
              duration: 0.14,
              ease: "power1.in",
            });
          }

          // Swap Content
          if (content) slot.textContent = content;

          // Spring In
          gsap.set(slot, { scale: 0.5, opacity: 0 });

          await gsap.to(slot, {
            scale: 1,
            opacity: 1,
            duration: 0.38,
            ease: "back.out(1.7)",
          });

          // Wait a bit before next state (unless it's the last one)
          if (i < sequence.length - 1) {
            await new Promise((r) => setTimeout(r, 95)); // Brief pause between flips
          }
        }
      };

      // Start Animation
      await new Promise((r) => setTimeout(r, 475));

      // Run animations in parallel with slight stagger
      const promises = allSlots.map((slot) => {
        const delay = Math.random() * 0.28;
        return animateChar(slot, delay);
      });

      await Promise.all(promises);

      // Wait for completion
      await new Promise((r) => setTimeout(r, 760));

      // 2. Move to Navbar Position
      let targetLogo = realLogoFull;
      if (realLogoFull && getComputedStyle(realLogoFull).display === "none") {
        targetLogo = realLogoShort;
      }
      // Fallback if neither found (shouldn't happen if Navbar is there)
      if (!targetLogo && realLogoShort) targetLogo = realLogoShort;

      if (targetLogo) {
        const startRect = loadingText.getBoundingClientRect();
        const endRect = targetLogo.getBoundingClientRect();

        // Lock current visual state
        gsap.set(loadingText, {
          position: "fixed",
          top: startRect.top,
          left: startRect.left,
          width: startRect.width,
          height: startRect.height,
          x: 0,
          y: 0,
          transform: "none",
        });

        // Animate to target
        gsap.to(loadingText, {
          top: endRect.top,
          left: endRect.left,
          fontSize: "2rem", // Shrink from 6rem to 2rem
          duration: 1.14,
          ease: "power3.inOut",
        });
      }

      // Wait for 80% of movement (approx 0.9s) to start overlap
      await new Promise((r) => setTimeout(r, 900));

      // 3. Fade out overlay and show real logo
      if (logoContainer) {
        logoContainer.style.transition = "none";
        gsap.to(logoContainer, { opacity: 1, duration: 0.76, ease: "power2.inOut" });
      }
      if (loadingText) {
        gsap.to(loadingText, { opacity: 0, duration: 0.76, ease: "power2.inOut" });
      }
      if (overlay) {
        gsap.to(overlay, { opacity: 0, duration: 0.76, ease: "power2.inOut" });
      }

      // Cleanup
      setTimeout(() => {
        setIsVisible(false);
        if (logoContainer) {
            // Restore transition for future interactions (e.g. hover)
            logoContainer.style.transition = "opacity 0.5s ease";
        }
      }, 950);
    };

    // Hide navbar logo initially
    const logoContainer = document.querySelector(".logo") as HTMLElement;
    if (logoContainer) {
        logoContainer.style.opacity = "0";
        logoContainer.style.transition = "opacity 0.5s ease";
    }

    animate();

    return () => {
      // Cleanup if unmounted (e.g. navigated away)
      if (logoContainer) {
        logoContainer.style.opacity = "1";
      }
      gsap.killTweensOf("#loading-text");
      gsap.killTweensOf(".char-slot");
    };
  }, [shouldPlay]);

  if (!shouldPlay || !isVisible) return null;

  return (
    <>
      <div id="overlay-layer"></div>
      <div id="loading-text">
        <div id="loading-line-1"></div>
        <div id="loading-line-2"></div>
      </div>
    </>
  );
}
